import { FormikErrors, useFormikContext } from 'formik';
import { useCallback } from 'react';

import { Badge } from '@@/Badge';

import { artifactConfigStepValidationSchema } from '../../validation';
import { WizardStep } from '../../WizardContext';
import { StepTitle } from '../StepTitle';
import { ArtifactConfig, FormValues } from '../../type';
import { DEPLOYMENT_TYPE_LABELS } from '../../detectDeploymentType';

import { AdvancedConfiguration } from './AdvancedConfiguration';
import { TargetsSelector } from './TargetsSelector';
import { UpdateConfiguration } from './UpdateConfiguration';
import { EnvironmentVariables } from './EnvironmentVariables';
import { RegistryConfiguration } from './RegistryConfiguration';

export const ArtifactConfigStep: WizardStep = {
  id: 'config',
  label: 'Config',
  Component: ArtifactConfigStepView,
  validateStep,
};

export function ArtifactConfigStepView() {
  const { values, setValues, setFieldError, errors } =
    useFormikContext<FormValues>();

  return (
    <div className="flex flex-col gap-8">
      {values.artifacts.map((artifact, index) => (
        <ArtifactConfigSection
          key={index}
          values={artifact}
          onChange={(next) =>
            setValues((prev) => ({
              ...prev,
              artifacts: prev.artifacts.map((a, i) => (i === index ? next : a)),
            }))
          }
          onRegistryError={(message) =>
            setFieldError(`artifacts[${index}].config.registryId`, message)
          }
          errors={getArtifactErrors(errors, index)}
        />
      ))}
    </div>
  );

  // Formik types nested array errors as `string | FormikErrors<T>[number]`
  // even though this schema never sets a plain string error at this path.
  function getArtifactErrors(
    formErrors: FormikErrors<FormValues>,
    index: number
  ) {
    const artifactErrors = formErrors.artifacts?.[index];
    return typeof artifactErrors === 'string' ? undefined : artifactErrors;
  }
}

function ArtifactConfigSection({
  errors,
  values,
  onChange,
  onRegistryError,
}: {
  errors: FormikErrors<FormValues['artifacts'][number]> | undefined;
  values: FormValues['artifacts'][number];
  onChange: (next: FormValues['artifacts'][number]) => void;
  onRegistryError: (message: string) => void;
}) {
  const setConfigValue = useCallback(
    <K extends keyof ArtifactConfig>(f: K, v: ArtifactConfig[K]) => {
      onChange({
        ...values,
        config: {
          ...values.config,
          [f]: v,
        },
      });
    },
    [values, onChange]
  );

  return (
    <section aria-label={`Configure ${values.name}`}>
      <StepTitle
        title={
          <>
            Configure {values.name}{' '}
            <Badge>{DEPLOYMENT_TYPE_LABELS[values.deploymentType]}</Badge>
          </>
        }
        subtitle="Choose the deployment targets and settings for this stack."
      />

      <div className="flex flex-col gap-4">
        <TargetsSelector
          values={values.targets}
          onChange={(targets) => onChange({ ...values, targets })}
          deploymentType={values.deploymentType}
        />

        <AdvancedConfiguration
          values={values.config}
          setFieldValue={setConfigValue}
          // TODO here we support only one file, we should probably show this only for values.files of type edge config or something like that?
          refName={values.files[0]?.ref}
          sourceId={values.files[0]?.sourceId}
          errors={errors?.config}
          deploymentType={values.deploymentType}
        />

        <EnvironmentVariables
          values={values.config.envVars}
          onChange={(value) => setConfigValue('envVars', value)}
        />

        <RegistryConfiguration
          values={values.config}
          setFieldValue={(f, v) => setConfigValue(f, v)}
          onRegistryError={onRegistryError}
        />

        <UpdateConfiguration
          values={values.config.parallelConfig}
          onChange={(config) =>
            setConfigValue('parallelConfig', {
              ...values.config.parallelConfig,
              ...config,
            })
          }
        />
      </div>
    </section>
  );
}

function validateStep() {
  return artifactConfigStepValidationSchema();
}
